/**
 * Authentication Service
 * Handles Azure Entra ID SSO authentication via MSAL
 *
 * DEPRECATED: Local JWT authentication has been replaced by Azure Entra ID SSO (AXIS175)
 */

import {
  msalInstance,
  loginRedirect,
  logout,
  acquireTokenSilent,
  getActiveAccount,
  isAuthenticated,
  getUserInfo,
} from '../config/msalConfig';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  status: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  display_name: string;
  message?: string;
}

export interface PasswordChangeRequest {
  user_id: string;
  new_password: string;
}

/**
 * AuthService
 * Manages Azure Entra ID SSO authentication
 *
 * Note: After AXIS175 migration, all authentication flows through MSAL and Azure Entra ID
 */
export class AuthService {
  /**
   * Login with Azure Entra ID SSO
   * Initiates OAuth2 authorization code flow with PKCE
   */
  static async login(_credentials?: LoginRequest): Promise<LoginResponse> {
    // Initiate Entra ID login redirect
    await loginRedirect();
    // Note: This function will redirect the browser to Entra ID login page
    // The response will be handled in the OAuth callback route

    // Return a placeholder (this code won't execute due to redirect)
    return {
      status: 'redirecting',
      user_id: '',
      email: '',
      first_name: '',
      last_name: '',
      role: '',
      display_name: '',
      message: 'Redirecting to Azure Entra ID login...',
    };
  }

  /**
   * Change password - DEPRECATED
   * Passwords are now managed by Azure Entra ID
   */
  static async changePassword(_request: PasswordChangeRequest): Promise<{ status: string; message: string; token?: string; user_id?: string; email?: string; role?: string; first_name?: string; last_name?: string }> {
    throw new Error(
      'Local password management has been disabled after Azure Entra ID migration. ' +
      'Passwords are now managed through Azure Entra ID. ' +
      'Please use the Entra ID password reset if needed.'
    );
  }

  /**
   * Logout user
   * Clears MSAL session and redirects to Entra ID logout
   */
  static async logout(): Promise<void> {
    await logout();
  }

  /**
   * Clear authentication state (alias for logout)
   */
  static async clearAuth(): Promise<void> {
    await this.logout();
  }

  /**
   * Get current user from MSAL account
   */
  static getCurrentUser() {
    const userInfo = getUserInfo();
    if (!userInfo) {
      return null;
    }

    return {
      id: userInfo.id,
      role: userInfo.roles[0] || '', // Get first role from Entra claims
      email: userInfo.email,
      first_name: userInfo.name?.split(' ')[0] || '',
      last_name: userInfo.name?.split(' ').slice(1).join(' ') || '',
      token: '', // Token is managed by MSAL, not stored locally
    };
  }

  /**
   * Check if user is logged in via MSAL
   */
  static isLoggedIn(): boolean {
    return isAuthenticated();
  }

  /**
   * Check if user is admin
   * Note: This checks the role claim from Entra ID token
   */
  static isAdmin(): boolean {
    const userInfo = getUserInfo();
    return userInfo?.roles.includes('admin') || false;
  }

  /**
   * Get MSAL access token for API calls
   * Acquires token silently or redirects to login if needed
   */
  static async getAccessToken(): Promise<string> {
    const result = await acquireTokenSilent();
    return result.accessToken;
  }

  /**
   * Get active MSAL account
   */
  static getActiveAccount() {
    return getActiveAccount();
  }

  /**
   * Get MSAL instance
   */
  static getMsalInstance() {
    return msalInstance;
  }
}
